/**
 * Página de detalhe da requisição (`/suprimentos/requisicoes/:id`) — versão
 * expandida do drawer (ADR-0069). É o alvo dos links do sino, de Minhas
 * Pendências e do e-mail de menção. Reusa `DetalheCorpo`; as ações de
 * situação passam pelo mesmo `mover-para` do quadro.
 */
import React, { useCallback, useEffect, useState } from "react";
import { FiCheck, FiDollarSign } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";

import EntityHeader from "../../../components/EntityHeader";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import {
  marcarSituacaoItem, moverRequisicaoPara, obterRequisicao, pessoasCompras,
} from "../../../services/compras";
import { SITUACAO_LABEL, SITUACAO_TONE } from "../../../services/requisicoesCompraUi";
import { PageContainer } from "../../../styles/shared";
import { Carregando, Vazio } from "../_shared/Estados";
import { DetalheCorpo } from "./Detalhe";
import GerarOcPanel from "./GerarOcPanel";
import { ModalMotivo } from "./ui";

const TEXTOS = {
  CANCELADA: { titulo: "Cancelar requisição", descricao: "Os itens abertos serão cancelados. Informe o motivo.", tone: "danger", confirmarLabel: "Cancelar requisição" },
  RASCUNHO: { titulo: "Voltar para 'A cotar'", confirmarLabel: "Voltar" },
  APROVADA: { titulo: "Reabrir como aprovada", confirmarLabel: "Reabrir" },
  ATENDIDA: { titulo: "Dar por recebida", rotulo: "Observação", obrigatorio: false, confirmarLabel: "Confirmar" },
};

export default function RequisicaoDetalhePagina() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { request, isMaster, hasAllPermissions } = useAuth();
  const toast = useToast();
  const podeGerir = isMaster || hasAllPermissions(["compras:criar_requisicao"]);
  const podeAprovar = isMaster || hasAllPermissions(["compras:aprovar_requisicao"]);

  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);
  const [pessoas, setPessoas] = useState([]);
  const [modal, setModal] = useState(null);
  const [gerarOc, setGerarOc] = useState(false);

  const carregar = useCallback(async () => {
    try {
      setReq(await obterRequisicao(request, id));
      setErro(false);
    } catch {
      setErro(true);
    } finally {
      setLoading(false);
    }
  }, [request, id]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    pessoasCompras(request).then((r) => setPessoas(r?.pessoas || [])).catch(() => setPessoas([]));
  }, [request]);

  async function mover(destino, extras = {}) {
    try {
      await moverRequisicaoPara(request, req.id, destino, extras);
      toast.success(`${req.numero} → ${SITUACAO_LABEL[destino] || destino}.`);
      await carregar();
      return true;
    } catch (err) {
      const precisa = err?.payload?.precisa;
      if (precisa === "motivo" || precisa === "observacao") { setModal({ destino, precisa }); return false; }
      if (precisa === "oc") { setGerarOc(true); return false; }
      toast.error(err?.payload?.detalhe || err?.message || "Falha na operação.");
      return false;
    }
  }

  async function mudarItem(item, situacao) {
    try {
      await marcarSituacaoItem(request, req.id, item.id, situacao);
      await carregar();
    } catch (err) {
      toast.error(err?.payload?.detalhe || err?.message || "Falha ao atualizar o item.");
    }
  }

  const situacao = req?.situacao;
  const terminal = situacao === "ATENDIDA" || situacao === "CANCELADA";
  const acoes = [];
  if (req && podeGerir && !terminal) acoes.push({ label: "Cancelar requisição", danger: true, onClick: () => setModal({ destino: "CANCELADA", precisa: "motivo" }) });
  if (req && podeGerir && terminal) acoes.push({ label: "Reabrir como aprovada", onClick: () => setModal({ destino: "APROVADA", precisa: "motivo" }) });
  if (req && podeGerir && (situacao === "EM_COTACAO" || situacao === "APROVADA")) acoes.push({ label: "Voltar para 'A cotar'", onClick: () => setModal({ destino: "RASCUNHO", precisa: "motivo" }) });
  if (req && podeGerir && situacao === "RASCUNHO") acoes.push({ label: "Marcar em andamento", onClick: () => mover("EM_COTACAO") });
  if (req && podeAprovar && situacao === "APROVADA") acoes.push({ label: "Dar por recebida (sem OC)", onClick: () => setModal({ destino: "ATENDIDA", precisa: "observacao" }) });

  let primaria;
  if (req && podeAprovar && (situacao === "RASCUNHO" || situacao === "EM_COTACAO")) primaria = { label: "Aprovar", icon: <FiCheck size={14} />, onClick: () => mover("APROVADA") };
  else if (req && podeGerir && situacao === "APROVADA") primaria = { label: "Gerar OC", icon: <FiDollarSign size={14} />, onClick: () => setGerarOc(true) };
  else if (req && podeAprovar && situacao === "AGUARDANDO_ENTREGA") primaria = { label: "Confirmar recebimento", icon: <FiCheck size={14} />, onClick: () => setModal({ destino: "ATENDIDA", precisa: "observacao" }) };

  return (
    <PageContainer>
      <EntityHeader
        onBack={() => navigate("/suprimentos/requisicoes")}
        backLabel="Requisições de Compra"
        title={req?.numero || "Requisição"}
        subtitle={req ? `${req.cliente_nome || "Sem cliente"}${req.projeto_resumo ? ` · ${req.projeto_resumo}` : ""}` : ""}
        badges={req ? [
          { key: "sit", label: SITUACAO_LABEL[situacao] || situacao, tone: SITUACAO_TONE[situacao] === "violet" || SITUACAO_TONE[situacao] === "teal" ? "info" : SITUACAO_TONE[situacao] },
          { key: "orig", label: req.origem_label, tone: "neutral" },
          ...(req.prioridade === "URGENTE" ? [{ key: "urg", label: "Urgente", tone: "danger" }] : []),
        ] : []}
        primaryAction={primaria}
        actions={acoes}
      />
      {loading ? <Carregando linhas={4} colunas={3} /> : erro || !req ? (
        <Vazio tone="danger" titulo="Requisição não encontrada" descricao="Ela pode ter sido excluída ou pertencer a outra unidade." acoes={[{ label: "Voltar à fila", onClick: () => navigate("/suprimentos/requisicoes") }]} />
      ) : (
        <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, padding: 24, boxShadow: "var(--shadow-1)" }}>
          <DetalheCorpo
            req={req}
            onRecarregar={carregar}
            onSituacaoItem={mudarItem}
            onGerarOc={() => setGerarOc(true)}
            podeGerir={podeGerir}
            pessoas={pessoas}
          />
        </div>
      )}

      <GerarOcPanel
        open={gerarOc}
        req={req}
        onClose={() => setGerarOc(false)}
        onGerada={async (oc) => { setGerarOc(false); toast.success(`OC ${oc?.numero || ""} gerada.`); await carregar(); }}
      />
      <ModalMotivo
        open={Boolean(modal)}
        {...(modal ? TEXTOS[modal.destino] || { titulo: "Confirmar" } : {})}
        onCancelar={() => setModal(null)}
        onConfirmar={async (texto) => {
          const extras = modal.precisa === "observacao" ? { observacao: texto } : { motivo: texto };
          const ok = await mover(modal.destino, extras);
          if (ok) setModal(null);
        }}
      />
    </PageContainer>
  );
}
